import { HttpClient, HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Action, State, StateContext } from "@ngxs/store";
import { createRequestAction, RequestState } from "ngxs-requests-plugin";
import { GetPersonDetails, GetPersonDetailsFail, GetPersonDetailsStream, GetPersonDetailsSuccess } from "./persons.actions";
import { environment } from "../../../../environments/environment";
import { AppConfigService } from "../../services/app-config.service";
import { LocalStorageService } from "../../services/local-storage.service";
import { fetchEventSource } from "@microsoft/fetch-event-source";
import { IPersonDetail } from "../../interfaces/persons.interface";

export interface PersonStateModel {
    personDetails:      { [hash: string] : IPersonDetail };
    loadingMessages:    { [hash: string]: string };
    dataIds:            { [hash: string]: string };
    hashes:             { [localRequestId: string]: string}
    lastRequestedHash:  string;
}

/**
 * Immutably updates a single property in a hash-based object
 * @param obj - The original object to update
 * @param hash - The key to update
 * @param value - The new value to set
 * @returns A new object with the updated value
 */
export function patchObjectByHash<T>(
  obj: { [hash: string]: T }, 
  hash: string, 
  value: T
): { [hash: string]: T } {
  return {
    ...obj,
    [hash]: value
  };
}

@RequestState('getPersonDetails')
@Injectable()
export class GetPersonDetailsRequestState {}

@State<PersonStateModel>({
    name: 'personState',
    defaults: {
        personDetails: {},
        loadingMessages: {},
        dataIds: {},
        hashes: {},
        lastRequestedHash: ''
    }
})
@Injectable()
export class PersonState {
    private personStreamAbortController: AbortController | null = null;

    constructor(
        private http: HttpClient,
        private localStorage: LocalStorageService,
        private appConfig: AppConfigService,
    ) {}

    @Action(GetPersonDetails)
    getPersonDetails(
        {dispatch}: StateContext<PersonStateModel>,
        {payload: {dataId, localRequestId}}: GetPersonDetails
    ) {
        const params = new HttpParams({ fromObject: { dataId, dbName: this.appConfig.dbName}});
        const request = this.http.get(environment.api + 'db/persons', {params});

        return dispatch(
            createRequestAction({
                request,
                state: GetPersonDetailsRequestState,
                successAction: GetPersonDetailsSuccess,
                failAction: GetPersonDetailsFail,
                metadata: {dataId, localRequestId}
            })
        );
    }

    @Action(GetPersonDetailsSuccess)
    getPersonDetailsSuccess(
        { dispatch, patchState, getState }: StateContext<PersonStateModel>,
        { payload: response, metadata: {dataId, localRequestId}}: GetPersonDetailsSuccess
    ) {
        if (response.status === 'finished') {
            const currentState = getState();
            // Parse the data if it's a string, otherwise use as-is
            const parsedData = response.content;
            const updatedPersonDetails = patchObjectByHash(currentState.personDetails, response.hash, parsedData);
            const updatedDataIds = patchObjectByHash(currentState.dataIds, response.hash, dataId);
            const updatedLoadingMessages = patchObjectByHash(currentState.loadingMessages, response.hash, 'done');
            const updatedHashes = patchObjectByHash(currentState.hashes, localRequestId, response.hash);

            patchState({
                personDetails: updatedPersonDetails, 
                loadingMessages: updatedLoadingMessages, 
                dataIds: updatedDataIds,
                hashes: updatedHashes,
                lastRequestedHash: response.hash
            });
        } else {
            const currentState = getState();
            const updatedHashes = patchObjectByHash(currentState.hashes, localRequestId, response.hash);
            
            patchState({
                hashes: updatedHashes
            });
            
            dispatch(new GetPersonDetailsStream({hash: response.hash, dataId, localRequestId}));
        }
    }

    @Action(GetPersonDetailsFail)
    getPersonDetailsFail(
        { patchState, getState }: StateContext<PersonStateModel>,
        { payload: response}: GetPersonDetailsFail
    ) {
        const currentState = getState();
        const errorMessage = response.error.content || 'An error occurred while fetching person details.';
        const updatedLoadingMessages = { 
            ...currentState.loadingMessages, 
            'unknown_hash': errorMessage 
        };
        patchState({loadingMessages: updatedLoadingMessages});
    }

    @Action(GetPersonDetailsStream)
    getPersonDetailsStream(
        { patchState, getState }: StateContext<PersonStateModel>,
        { payload: {hash, dataId, localRequestId} }: GetPersonDetailsStream
    ) {
        if (this.personStreamAbortController) {
            this.personStreamAbortController.abort();
        }
        this.personStreamAbortController = new AbortController();
        const currentCtrl = this.personStreamAbortController;

        const token = this.localStorage.getItem('sequenza_token');

        fetchEventSource(environment.api + 'db/persons/stream/' + hash, {
            headers: { "Authorization": `Bearer ${token}` },
            signal: currentCtrl.signal,
            onmessage: (e) => {
                if (e.event === "finished") {
                    const personDetails = JSON.parse(e.data);
                    const currentState = getState();
                    const updatedPersonDetails = patchObjectByHash(currentState.personDetails, hash, personDetails);
                    const updatedLoadingMessages = patchObjectByHash(currentState.loadingMessages, hash, 'done');
                    patchState({
                        personDetails: updatedPersonDetails,
                        loadingMessages: updatedLoadingMessages
                    });
                    currentCtrl.abort();
                } else if (e.event === "updated") {
                    const currentState = getState();
                    const updatedLoadingMessages = { 
                        ...currentState.loadingMessages, 
                        [hash]: e.data
                    };
                    patchState({
                        loadingMessages: updatedLoadingMessages
                    });
                } else if (e.event === "invalid") {
                    const currentState = getState();
                    const errorData = e.data || 'Invalid person ID';
                    const updatedLoadingMessages = { 
                        ...currentState.loadingMessages, 
                        [hash]: errorData
                    };
                    patchState({
                        loadingMessages: updatedLoadingMessages
                    });
                    currentCtrl.abort();
                } else if (e.event === "error") {
                    const currentState = getState();
                    const errorData = e.data || 'An error occurred while streaming person details.';
                    const updatedLoadingMessages = { 
                        ...currentState.loadingMessages, 
                        [hash]: errorData
                    };
                    patchState({
                        loadingMessages: updatedLoadingMessages
                    });
                    currentCtrl.abort();
                }
            },
            onerror: (err) => {
                if (err.name === 'AbortError') {
                    return;
                }
                const currentState = getState();
                const updatedLoadingMessages = { 
                    ...currentState.loadingMessages, 
                    [hash]: 'An error occurred while streaming person details.'
                };
                patchState({
                    loadingMessages: updatedLoadingMessages
                });
                currentCtrl.abort();
                throw err;
            }
        });
    }   
}